import { Stack, Typography } from '@mui/material'
import { Dispatch, SetStateAction, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmationDialog from '../../components/Dialog/ConfirmationDialog'
import { useNotification } from '../../store/redux/hooks'
import { useDeleteEmployeeVacationsMutation } from '../../store/redux/reducers/vacationSlice'
import { SelectedDataState } from '../../store/types/components/table/vacationTable'
import { createVacationErrorMessage } from '../../store/utils/createErrorMessage'
import { formatStringDate } from '../../store/utils/dateUtils'

type DeleteVacationDialogProps = {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  onClose: () => void
} & Pick<SelectedDataState, 'displayStart' | 'displayEnd' | 'vacationId' | 'secondSelectedEmployee'>

const DeleteVacationDialog = ({
  isOpen,
  setIsOpen,
  onClose,
  displayStart,
  displayEnd,
  vacationId,
  secondSelectedEmployee,
}: DeleteVacationDialogProps) => {
  const { t } = useTranslation()
  const { displayNotification } = useNotification()
  const [deleteEntry, { isLoading }] = useDeleteEmployeeVacationsMutation()

  const deleteEmployeeVacation = useCallback(
    async (startDate: string, endDate: string, employeeId: string, vacationId: string) => {
      try {
        await deleteEntry({
          dto: {
            startDate,
            endDate,
            employeeId,
            vacationId,
          },
        }).unwrap()
        onClose()
      } catch (error) {
        displayNotification({
          open: false,
          type: 'error',
          message: createVacationErrorMessage(error),
          timeout: 5000,
        })
      }
    },
    [deleteEntry, onClose, displayNotification]
  )

  const handleDeleteConfirm = () => {
    if (displayStart && displayEnd && secondSelectedEmployee && vacationId) {
      deleteEmployeeVacation(displayStart, displayEnd, secondSelectedEmployee.id, vacationId)
    }
  }

  const renderConfirmationDialogContent = () => (
    <Stack direction="row" spacing={0.5}>
      <Typography>{t('page.vacationView.messages.deleteVacationConfirm')}</Typography>
      <Typography fontWeight={500}>
        {displayStart === displayEnd
          ? formatStringDate(displayStart)
          : `${formatStringDate(displayStart)} - ${formatStringDate(displayEnd)}`}
      </Typography>
    </Stack>
  )

  return (
    <ConfirmationDialog
      open={isOpen}
      setOpen={setIsOpen}
      handleClose={onClose}
      handleConfirm={handleDeleteConfirm}
      loading={isLoading}
      title={t('page.vacationView.messages.deleteVacation')}
      content={renderConfirmationDialogContent()}
    />
  )
}

export default DeleteVacationDialog
